/**
 * THE PRICE CARD'S WORDS, BY EDITION (docs/PLAN-per-employee-pricing-20261030.md: C-2, C-14, R6).
 *
 * One owner for the words every surface that sells or changes a plan says about the price card a plan is on: what a
 * seat is called on each edition's ladder, the lock, the meeting past the ladder's last rung, and the plain line for each
 * of the resolver's refusal codes. Read by both designs of /subscription (through src/hooks/subscriptionPageModel.ts),
 * the hire dialog (src/components/subscription/useHireConfirmBody.ts), the renewal terms (./autoRenewalTerms.ts), the
 * agreement journey and /pricing's hire card (src/hooks/useHirePicker.ts), so a legacy plan is never described in
 * another edition's words and a refusal reads the same wherever it lands.
 *
 * Editions 2026-11 and 2026-12 sell "Prest0n employees" priced "per employee" (C-14); edition 2026-08's seats were "Prest0ns" priced
 * "per Prest0n", and a subscription signed on it keeps those words with its own ladder for as long as it stays active.
 *
 * Leaf module: it imports only the tier vocabulary and the employee unit (both leaves), so the renewal terms read it
 * without the subscription page's model, which loads firebase.
 */
import { EMPLOYEE_UNIT_NAME, EMPLOYEE_UNIT_PLURAL } from './employeeUnit.generated';
import { PUBLISHED_EDITION, maxSeatsOf, type CatalogEdition, type ResolveRefusal } from './planTiers';

export interface SeatWords {
  /** One seat: "Prest0n employee" (edition 2026-11), "Prest0n" (2026-08). */
  one: string;
  many: string;
  /** The plural where the name is already said: "employees", "Prest0ns". */
  short: string;
  /** The unit a price is quoted per: "per employee", "per Prest0n". */
  per: string;
}

const EMPLOYEE_WORDS: SeatWords = { one: EMPLOYEE_UNIT_NAME, many: EMPLOYEE_UNIT_PLURAL, short: 'employees', per: 'per employee' };
const PROFESSIONAL_WORDS: SeatWords = { one: 'Prest0n', many: 'Prest0ns', short: 'Prest0ns', per: 'per Prest0n' };

/** What a seat is called on each edition's ladder (every edition of the vocabulary has its words; a test holds that). */
export const SEAT_WORDS: Readonly<Record<CatalogEdition, SeatWords>> = {
  '2026-08': PROFESSIONAL_WORDS,
  '2026-11': EMPLOYEE_WORDS,
  '2026-12': EMPLOYEE_WORDS,
};

/** The seat words of an edition; a plan on no edition (none yet, a grandfathered plan) reads in the published one's. */
export function seatWordsFor(edition: CatalogEdition | null | undefined): SeatWords {
  return SEAT_WORDS[edition ?? PUBLISHED_EDITION] ?? SEAT_WORDS[PUBLISHED_EDITION];
}

/** "3 Prest0n employees", "1 Prest0n". */
export function seatCount(n: number, w: SeatWords): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? w.one : w.many}`;
}

/**
 * The lock (R6), said as the card: the price card a subscription signed on never rises while it stays active; adding
 * seats moves the team to that size's rung of the same card, and fewer seats, or monthly, are the card's own (dearer)
 * rung. Said beside every hire and every change, and in the renewal terms.
 */
export function lockLine(w: SeatWords): string {
  return `your price card is locked for as long as you stay; fewer ${w.short} or monthly use the card’s own rung.`;
}

/** Where a team past the ladder's last rung books its meeting. */
export const MEETING_HREF = '/demo';

/** The first team size an edition does not sell on its own. */
export function meetingFrom(edition: CatalogEdition | null | undefined): number {
  return maxSeatsOf(edition ?? PUBLISHED_EDITION) + 1;
}

/** "300 or more employees: book a meeting" (the published ladder runs to 299, R4). */
export function meetingLine(edition: CatalogEdition | null | undefined): string {
  return `${meetingFrom(edition).toLocaleString('en-US')} or more ${seatWordsFor(edition).short}: book a meeting`;
}

/** The meeting as a link a page draws: its words and where it goes. */
export function meetingLink(edition: CatalogEdition | null | undefined): { label: string; href: string } {
  return { label: meetingLine(edition), href: MEETING_HREF };
}

/**
 * The plain line for each refusal the resolver gives (C-2: resolveHire, resolveChange in the tier vocabulary), which
 * the server's hire and change doors answer as `{ error, code }`. Failure copy (D9): it says what happened and what to
 * do next, never the code.
 */
const REFUSAL_WORDS: Readonly<Record<ResolveRefusal, (edition: CatalogEdition) => string>> = {
  'bad-cadence': () => 'choose how you want to be billed — monthly, annual or the prepaid year — then try again.',
  'bad-seats': (edition) => `choose a team of 1 to ${maxSeatsOf(edition).toLocaleString('en-US')} ${seatWordsFor(edition).short}, then try again.`,
  'by-meeting': (edition) => `${meetingLine(edition)}.`,
  'tier-mismatch': () => 'the price for this team was quoted again — check the new figures, then confirm.',
  'edition-not-on-sale': () => 'new prices are not on sale yet; try again shortly.',
  'cross-edition': () => 'this plan keeps its own price card.',
};

/** Every refusal code the resolver gives, read off the words table (TypeScript holds the table to the vocabulary's type). */
export const PLAN_REFUSALS: readonly ResolveRefusal[] = Object.keys(REFUSAL_WORDS) as ResolveRefusal[];

/** A refusal code from a server answer, or null for anything else. */
export function asPlanRefusal(code: unknown): ResolveRefusal | null {
  return typeof code === 'string' && (PLAN_REFUSALS as readonly string[]).includes(code) ? (code as ResolveRefusal) : null;
}

/** The plain line for a refusal, in the words of the edition the plan is on (the published one for a hire). */
export function planRefusalLine(code: ResolveRefusal, edition: CatalogEdition | null | undefined = PUBLISHED_EDITION): string {
  return REFUSAL_WORDS[code](edition ?? PUBLISHED_EDITION);
}
